import { approvedVendorsQuery } from "@common/api"
import type { VendorStatus } from "@common/client"
import { PaginationControls } from "@common/components/shared/pagination-controls"
import { Button } from "@common/components/ui/button"
import { Skeleton } from "@common/components/ui/skeleton"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { ShieldCheck } from "lucide-react"
import { Suspense } from "react"
import { z } from "zod"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import {
  calculateSkip,
  calculateTotalPages,
  getPageNumbers,
} from "@/lib/pagination"
import { statusName } from "./-components/status-dot"
import {
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  VendorTable,
} from "./-components/vendor-table"

const ITEMS_PER_PAGE = 20

const statusFilterSchema = z.enum([
  "all",
  "active",
  "inactive",
  "expired",
  "archived",
])
type StatusFilter = z.infer<typeof statusFilterSchema>

function statusFilterLabel(filter: StatusFilter): string {
  switch (filter) {
    case "all":
      return "All"
    case "archived":
      return "Archived"
    default:
      return statusName(filter)
  }
}

function statusFilterParams(filter: StatusFilter): {
  status: VendorStatus | null
  archived: boolean
} {
  switch (filter) {
    case "active":
      return { status: "active", archived: false }
    case "inactive":
      return { status: "inactive", archived: false }
    case "expired":
      return { status: "expired", archived: false }
    case "archived":
      return { status: null, archived: true }
    case "all":
      return { status: null, archived: false }
  }
}

function listParams(page: number, search: string, status: StatusFilter) {
  return {
    skip: calculateSkip(page, ITEMS_PER_PAGE),
    limit: ITEMS_PER_PAGE,
    search: search || null,
    ...statusFilterParams(status),
  }
}

const filterSchema = z.object({
  page: z.number().optional().catch(1),
  search: z.string().optional().catch(""),
  status: statusFilterSchema.optional().catch("all"),
})

export const Route = createFileRoute("/_layout/approved-vendors")({
  staticData: { breadcrumb: "Approved vendors" },
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search: { page = 1, search = "", status = "all" } }) => ({
    page,
    search,
    status,
  }),
  loader: async ({
    context: { queryClient },
    deps: { page, search, status },
  }) => {
    await queryClient.ensureQueryData(
      approvedVendorsQuery(listParams(page, search, status)),
    )
  },
  component: ApprovedVendorsPage,
})

function ApprovedVendorsPage(): React.ReactElement {
  const { page = 1, search = "", status = "all" } = Route.useSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(COLUMNS, COLUMN_SIZING)
  const fit = fitColumns([...COLUMNS], width, sizing)

  return (
    <ListPage
      toolbar={<Toolbar search={search} status={status} />}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <Suspense fallback={<VendorListSkeleton />}>
        <VendorResults
          page={page}
          search={search}
          status={status}
          columns={fit.shown}
        />
      </Suspense>
    </ListPage>
  )
}

function Toolbar({
  search,
  status,
}: {
  search: string
  status: StatusFilter
}): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const canCreate = useHasPermission("approved_vendor:create")
  const selectStatus = (value: StatusFilter) =>
    navigate({
      search: (prev) => ({
        ...prev,
        status: value === "all" ? undefined : value,
        page: 1,
      }),
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={statusFilterSchema.options}
          tab={status}
          label={statusFilterLabel}
          name="Vendor status"
          onSelect={selectStatus}
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: 1,
                  search: next || undefined,
                }),
              })
            }
            placeholder="Search vendors…"
          />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/approved-vendors/new" search={true}>
                Add vendor
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function VendorResults({
  page,
  search,
  status,
  columns,
}: {
  page: number
  search: string
  status: StatusFilter
  columns: Column[]
}): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { data } = useSuspenseQuery(
    approvedVendorsQuery(listParams(page, search, status)),
  )

  if (data.count === 0) {
    return <ListEmpty icon={ShieldCheck} {...emptyStateCopy(search, status)} />
  }

  const totalPages = calculateTotalPages(data.count, ITEMS_PER_PAGE)
  const pageNumbers = getPageNumbers(page, totalPages)

  return (
    <>
      <VendorTable vendors={data.data} columns={columns} />
      <div className="px-8 py-6">
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          pageNumbers={pageNumbers}
          onPageChange={(next) =>
            navigate({
              search: (prev) => ({ ...prev, page: next }),
            })
          }
        />
      </div>
    </>
  )
}

function emptyStateCopy(
  search: string,
  status: StatusFilter,
): { title: string; body: string } {
  if (search) {
    return {
      title: "No matching vendors",
      body: "Try a different search term.",
    }
  }
  if (status === "archived") {
    return {
      title: "No archived vendors",
      body: "Archived vendors appear here.",
    }
  }
  return {
    title: "No approved vendors",
    body: "Add a vendor to start building your approved vendor list.",
  }
}

function VendorListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-2 px-4 py-4">
      {[1, 2, 3, 4, 5, 6].map((index) => (
        <Skeleton key={index} className="h-12 w-full rounded-md" />
      ))}
    </div>
  )
}
